'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useFile, useUpdateFile } from '@/hooks'
import { LoadingState } from '@/components/partials'
import FileForm, { type FileFormRef } from './form'
import { CreateFileParams } from '@/types/file'
import { FormModeEnum } from '@/enums'

type PropsType = {
  id: string
}

const Update = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const formRef = useRef<FileFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: file, isLoading: isLoadingFile, error: fileError } = useFile(id)
  const { mutate: updateFile, isPending: isUpdating, uploadProgress } = useUpdateFile()

  const handleSubmit = (data: CreateFileParams) => {
    // Ensure the name is always a string, use the file's current name as fallback
    const name = data.name || file?.name || ''

    updateFile(
      {
        id: id,
        params: {
          name,
          file: data.file,
          optimize: data.optimize,
          accessLevel: data.accessLevel
        }
      },
      {
        onSuccess: async updatedFile => {
          setIsRedirecting(true)
          queryClient.setQueryData(['file', id], updatedFile)
          await queryClient.invalidateQueries({ queryKey: ['files'] })
          toast.success(t('updated_successfully'))
          router.push(`/files/${id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    console.error('Error updating file:', error)

    if (formRef.current) {
      formRef.current.handleValidationErrors(error)
    }

    if (error.response?.status !== 422) {
      toast.error(t('error_updating'))
    }
  }

  const handleCancel = () => {
    router.push(`/files/${id}`)
  }

  return (
    <LoadingState isLoading={isLoadingFile} error={fileError?.message}>
      {file && (
        <FileForm
          ref={formRef}
          mode={FormModeEnum.UPDATE}
          initialFile={file}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isUpdating || isRedirecting}
          uploadProgress={uploadProgress}
          optimizeImageSelection={true}
        />
      )}
    </LoadingState>
  )
}

export default Update
